import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  PhoneOff,
  Radio,
  Send,
  UserCircle,
  Users2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  type BroadcastDto,
  type SendBroadcastInput,
  useBroadcast,
  useBroadcastsList,
  useSendBroadcast,
} from '../api/useBroadcasts.js';
import { type RosterMember, useRoster } from '../api/useRoster.js';
import { type TeamDto, useTeamsList } from '../api/useTeams.js';
import { EmptyState } from '../components/EmptyState.js';
import { cn } from '../lib/cn.js';
import { formatDateTime, formatRelative } from '../lib/format.js';

// Broadcasts page — composer (top) + history (bottom).
//
// The audience picker supports three non-exclusive targeting modes, matching the API:
//   • Teams        — chip multi-select; resolves to all workers on picked teams
//   • Workers      — individual multi-select via searchable list
//   • Emergency contacts — existing fanout ("all" toggle or explicit picker later)
//
// The preview counts recipients client-side by resolving team memberships from the
// roster's `teams[]`. This is only a hint; the authoritative count is whatever the
// API returns from the POST.

const QUICK_TEMPLATES: { label: string; text: string }[] = [
  { label: 'Shift cover needed', text: 'URGENT: Shift cover needed. Please reply if available.' },
  { label: 'Check in status', text: 'Please confirm your location and status in the app now.' },
  {
    label: 'Safety alert',
    text: 'SAFETY ALERT: unusual activity reported in your area. Stay vigilant.',
  },
];

export default function Broadcasts() {
  const [message, setMessage] = useState('');
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [sendToAllContacts, setSendToAllContacts] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const send = useSendBroadcast();
  const list = useBroadcastsList();
  const teams = useTeamsList();
  const roster = useRoster();

  const broadcasts = list.data?.broadcasts ?? [];
  const allTeams = teams.data?.teams ?? [];
  const allMembers = roster.data?.members ?? [];

  // Preview: resolve teams → worker user ids (client-side), union with explicit userIds,
  // and count workers with a phone number. Not authoritative — API dedups by phone too
  // and drops workers without phones — but close enough for a UI hint.
  const preview = useMemo(
    () => resolveAudiencePreview(allMembers, teamIds, userIds),
    [allMembers, teamIds, userIds],
  );

  const canSend = Boolean(message.trim()) && (preview.count > 0 || sendToAllContacts);

  async function onSend() {
    if (!canSend) return;
    const input: SendBroadcastInput = { message: message.trim() };
    if (teamIds.length > 0) input.team_ids = teamIds;
    if (userIds.length > 0) input.user_ids = userIds;
    if (sendToAllContacts) input.send_to_all_contacts = true;
    try {
      const result = await send.mutateAsync(input);
      setMessage('');
      setTeamIds([]);
      setUserIds([]);
      setSendToAllContacts(false);
      setExpanded(result.broadcast.id);
    } catch {
      // surfaced below via send.error
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <span className="eyebrow">Operations</span>
        <h1 className="mt-1 font-heading text-[22px] font-bold tracking-tightest text-ink">
          Broadcasts
        </h1>
        <p className="text-sm text-ink-faint">
          Send SMS to a team, to selected workers, or to all emergency contacts. Messages are
          deduped by phone number across targets.
        </p>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2 className="font-heading text-sm font-bold text-ink">New broadcast</h2>
            <span className="eyebrow">Compose · pick audience · review</span>
          </div>
        </div>
        <div className="panel-body">
          <AudiencePicker
            teams={allTeams}
            members={allMembers}
            teamIds={teamIds}
            userIds={userIds}
            sendToAllContacts={sendToAllContacts}
            onChange={(next) => {
              if (next.teamIds !== undefined) setTeamIds(next.teamIds);
              if (next.userIds !== undefined) setUserIds(next.userIds);
              if (next.sendToAllContacts !== undefined)
                setSendToAllContacts(next.sendToAllContacts);
            }}
            preview={preview}
          />

          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between">
              <label htmlFor="bc-msg" className="text-xs font-medium text-ink-faint">
                Message
              </label>
              <div className="flex flex-wrap gap-2">
                {QUICK_TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => setMessage(t.text)}
                    className="rounded-md border border-line bg-surface-3 px-2.5 py-0.5 text-[11px] text-ink-dim transition hover:bg-surface-3"
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              id="bc-msg"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="e.g. Are you safe to continue your shift?"
              rows={3}
              maxLength={1000}
              className="w-full resize-none rounded-md border border-line-strong px-3 py-2 text-sm placeholder-gray-400 focus:border-brand-purple focus:outline-none focus:ring-1 focus:ring-brand-purple"
            />
          </div>

          <div className="mt-2 flex items-center justify-between text-xs text-ink-faint">
            <span>
              {message.length} / 1000 chars · {Math.ceil(Math.max(message.length, 1) / 160)} SMS
              segment
              {Math.ceil(Math.max(message.length, 1) / 160) === 1 ? '' : 's'}
            </span>
            <button
              type="button"
              onClick={onSend}
              disabled={send.isPending || !canSend}
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-purple/90 disabled:opacity-50"
            >
              {send.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {send.isPending
                ? 'Sending…'
                : preview.count > 0 || sendToAllContacts
                  ? `Send broadcast${preview.count > 0 ? ` (${preview.count})` : ''}`
                  : 'Pick an audience'}
            </button>
          </div>

          {preview.missingPhoneCount > 0 && preview.count > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <PhoneOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {preview.missingPhoneCount}{' '}
                {preview.missingPhoneCount === 1 ? 'worker is' : 'workers are'} skipped — no phone
                number on file.
              </span>
            </div>
          )}

          {send.isError && (
            <div className="mt-3 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{(send.error as Error).message}</span>
            </div>
          )}
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2 className="font-heading text-sm font-bold text-ink">History</h2>
            <span className="eyebrow">Last 100 broadcasts</span>
          </div>
        </div>

        {list.isError && (
          <div className="mx-6 my-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            Failed to load broadcasts: {(list.error as Error).message}
          </div>
        )}

        {!list.isLoading && broadcasts.length === 0 && (
          <EmptyState
            icon={Radio}
            title="No broadcasts yet"
            description="Your first SMS broadcast will show here with per-recipient delivery status."
          />
        )}

        {broadcasts.length > 0 && (
          <ul className="divide-y divide-line">
            {broadcasts.map((b) => (
              <BroadcastRow
                key={b.id}
                b={b}
                expanded={expanded === b.id}
                onToggle={() => setExpanded((prev) => (prev === b.id ? null : b.id))}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

interface Preview {
  count: number;
  missingPhoneCount: number;
  uniqueUserIds: Set<string>;
}

function resolveAudiencePreview(
  members: RosterMember[],
  teamIds: string[],
  userIds: string[],
): Preview {
  if (teamIds.length === 0 && userIds.length === 0) {
    return { count: 0, missingPhoneCount: 0, uniqueUserIds: new Set() };
  }
  const teamSet = new Set(teamIds);
  const explicitSet = new Set(userIds);
  const hit = new Set<string>();
  for (const m of members) {
    if (explicitSet.has(m.user_id) || m.teams.some((t) => teamSet.has(t.id))) {
      hit.add(m.user_id);
    }
  }
  let withPhone = 0;
  let missing = 0;
  for (const id of hit) {
    const m = members.find((x) => x.user_id === id);
    if (m?.phone_e164) withPhone += 1;
    else missing += 1;
  }
  return { count: withPhone, missingPhoneCount: missing, uniqueUserIds: hit };
}

function AudiencePicker({
  teams,
  members,
  teamIds,
  userIds,
  sendToAllContacts,
  onChange,
  preview,
}: {
  teams: TeamDto[];
  members: RosterMember[];
  teamIds: string[];
  userIds: string[];
  sendToAllContacts: boolean;
  onChange: (
    next: Partial<{ teamIds: string[]; userIds: string[]; sendToAllContacts: boolean }>,
  ) => void;
  preview: Preview;
}) {
  const [showPicker, setShowPicker] = useState(false);
  const [filter, setFilter] = useState('');
  const teamSet = new Set(teamIds);
  const explicitSet = new Set(userIds);

  const workers = members.filter((m) => m.app_role === 'worker');
  const shown = useMemo(() => {
    const f = filter.toLowerCase();
    const notOnTeam = workers.filter((m) => !m.teams.some((t) => teamSet.has(t.id)));
    return f
      ? notOnTeam.filter(
          (m) => m.display_name.toLowerCase().includes(f) || m.email.toLowerCase().includes(f),
        )
      : notOnTeam;
  }, [workers, filter, teamSet]);

  function toggleTeam(id: string) {
    const next = new Set(teamSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ teamIds: [...next] });
  }

  function toggleUser(id: string) {
    const next = new Set(explicitSet);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange({ userIds: [...next] });
  }

  function clearAll() {
    onChange({ teamIds: [], userIds: [], sendToAllContacts: false });
  }

  return (
    <div className="rounded-lg border border-line bg-surface-3 p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-ink-faint">
          <Users2 className="h-3.5 w-3.5" />
          Audience
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span
            className={cn(
              'font-medium',
              preview.count > 0 || sendToAllContacts ? 'text-ink' : 'text-ink-mute',
            )}
          >
            {preview.count > 0
              ? `${preview.count} ${preview.count === 1 ? 'worker' : 'workers'}`
              : 'none'}
            {sendToAllContacts ? ' · all contacts' : ''}
          </span>
          {(teamIds.length > 0 || userIds.length > 0 || sendToAllContacts) && (
            <button type="button" onClick={clearAll} className="text-ink-faint hover:text-ink">
              Clear
            </button>
          )}
        </div>
      </div>

      {teams.length === 0 && (
        <p className="mb-2 rounded-md bg-white px-3 py-2 text-xs text-ink-faint">
          No teams yet.{' '}
          <a href="/#/teams" className="font-medium text-brand-purple hover:underline">
            Create one
          </a>{' '}
          to broadcast by discipline.
        </p>
      )}

      {teams.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {teams.map((t) => {
            const active = teamSet.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleTeam(t.id)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition',
                  active
                    ? 'border-brand-purple bg-brand-purple text-white'
                    : 'border-line bg-white text-ink-dim hover:border-line-strong',
                )}
              >
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ backgroundColor: active ? '#ffffff' : (t.colour ?? '#4C12A1') }}
                />
                {t.name}
                <span
                  className={cn('ml-0.5 text-[10px]', active ? 'text-white/80' : 'text-ink-mute')}
                >
                  {t.member_count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setShowPicker((v) => !v)}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-white px-2.5 py-1 text-xs font-medium text-ink-dim hover:bg-surface-3"
        >
          <UserCircle className="h-3.5 w-3.5" />
          {userIds.length > 0
            ? `${userIds.length} worker${userIds.length === 1 ? '' : 's'} selected`
            : 'Add individual workers'}
        </button>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-xs text-ink-dim">
          <input
            type="checkbox"
            checked={sendToAllContacts}
            onChange={(e) => onChange({ sendToAllContacts: e.target.checked })}
            className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple focus:ring-brand-purple"
          />
          Also SMS every emergency contact
        </label>
      </div>

      {showPicker && (
        <div className="mt-3 rounded-md border border-line bg-white p-3">
          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter workers by name or email"
            className="w-full rounded-md border border-line-strong px-3 py-1.5 text-sm focus:border-brand-purple focus:outline-none"
          />
          <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-line">
            {shown.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-ink-faint">
                {workers.length === 0
                  ? 'No workers yet.'
                  : 'No workers match — already covered by selected teams.'}
              </div>
            ) : (
              <ul className="divide-y divide-line">
                {shown.map((m) => {
                  const picked = explicitSet.has(m.user_id);
                  const noPhone = !m.phone_e164;
                  return (
                    <li key={m.user_id}>
                      <label
                        className={cn(
                          'flex cursor-pointer items-center gap-2 px-2.5 py-1.5 text-sm hover:bg-surface-3',
                          noPhone && 'opacity-60',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={picked}
                          onChange={() => toggleUser(m.user_id)}
                          className="h-3.5 w-3.5 rounded border-line-strong text-brand-purple focus:ring-brand-purple"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-xs font-medium text-ink">
                            {m.display_name}
                          </div>
                          <div className="truncate text-[11px] text-ink-faint">{m.email}</div>
                        </div>
                        {noPhone ? (
                          <span className="inline-flex items-center gap-1 text-[10px] text-amber-700">
                            <PhoneOff className="h-3 w-3" />
                            no phone
                          </span>
                        ) : (
                          <span className="font-mono text-[10px] text-ink-faint">
                            {m.phone_e164}
                          </span>
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BroadcastRow({
  b,
  expanded,
  onToggle,
}: {
  b: BroadcastDto;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-4 px-6 py-4 text-left transition hover:bg-surface-3"
      >
        <div className="flex-1">
          <div className="mb-1 flex items-center gap-2 text-xs text-ink-faint">
            <Clock className="h-3 w-3" />
            {formatRelative(b.sent_at)} · {formatDateTime(b.sent_at)}
          </div>
          <div className="mb-1 text-sm text-ink">“{b.message}”</div>
          <div className="flex items-center gap-3 text-xs">
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="h-3 w-3" /> {b.delivered_count} / {b.recipient_count} sent
            </span>
            {b.failed_count > 0 && (
              <span className="inline-flex items-center gap-1 text-red-700">
                <AlertTriangle className="h-3 w-3" /> {b.failed_count} failed
              </span>
            )}
          </div>
        </div>
      </button>
      {expanded && <RecipientTable broadcastId={b.id} />}
    </li>
  );
}

function RecipientTable({ broadcastId }: { broadcastId: string }) {
  const { data, isLoading, isError, error } = useBroadcast(broadcastId);
  if (isLoading) {
    return (
      <div className="border-t border-line bg-surface-3 px-6 py-3 text-xs text-ink-faint">
        Loading recipients…
      </div>
    );
  }
  if (isError) {
    return (
      <div className="border-t border-red-200 bg-red-50 px-6 py-3 text-xs text-red-700">
        {(error as Error).message}
      </div>
    );
  }
  const recipients = data?.recipients ?? [];
  if (recipients.length === 0) {
    return (
      <div className="border-t border-line bg-surface-3 px-6 py-3 text-xs text-ink-faint">
        No recipients.
      </div>
    );
  }
  return (
    <div className="border-t border-line bg-surface-3 px-6 py-3">
      <table className="min-w-full divide-y divide-line text-xs">
        <thead>
          <tr>
            <th className="py-1 text-left font-medium uppercase tracking-wider text-ink-faint">
              Kind
            </th>
            <th className="py-1 text-left font-medium uppercase tracking-wider text-ink-faint">
              Phone
            </th>
            <th className="py-1 text-left font-medium uppercase tracking-wider text-ink-faint">
              Status
            </th>
            <th className="py-1 text-left font-medium uppercase tracking-wider text-ink-faint">
              Provider ID
            </th>
            <th className="py-1 text-left font-medium uppercase tracking-wider text-ink-faint">
              Error
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {recipients.map((r) => (
            <tr key={r.id}>
              <td className="py-1 text-ink-dim">
                {r.user_id ? 'worker' : r.contact_id ? 'contact' : 'phone'}
              </td>
              <td className="py-1 font-mono">{r.phone_e164}</td>
              <td className="py-1">
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
                    r.status === 'sent' || r.status === 'delivered'
                      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                      : r.status === 'failed'
                        ? 'bg-red-50 text-red-700 ring-red-200'
                        : 'bg-surface-3 text-ink-dim ring-gray-200',
                  )}
                >
                  {r.status}
                </span>
              </td>
              <td className="py-1 font-mono text-ink-faint">
                {r.provider_msg_id ? `${r.provider_msg_id.slice(0, 12)}…` : '—'}
              </td>
              <td className="py-1 text-red-600">{r.error_message ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
